// src/oura/components/docs/create/CreateDocSheet.tsx
//
// Create a doc (B12, scope 2.2): a sheet inside the documents board's one
// drawer, never a drawer of its own. Staff pick the doc type, how it is made,
// its codes (and outlet) and its folder, and see the file name and page title
// it will get in the decided format as they pick (B16's format, live).
//
//   Header   "Create the {doc type}", its code chips, and "● Not shared ·
//            Read it before you share": a new doc is never shared on create.
//   Body     1 Doc type: the template list (1 per catalogue doc type), then
//              Custom doc type (ourteam-backend#6516): staff name the type,
//              pick any of the plan's codes and, if they want, an outlet, and
//              make it blank, from a link or from a Word file;
//            2 How: from the template, from a Google Doc we have (its link),
//              from a Word file in the folder (converted), or by running its
//              skill (#3983): "Write it with the skill" once one is added for
//              the doc type, with an optional note; shut until then, with
//              where it plugs in and what Oura sends it;
//            3 Codes, and the outlet for a per-outlet doc;
//            4 Folder: a folder the client can open is listed, and Create is
//              refused there in plain words;
//            5 Name: the file name and lines 1 and 2, live.
//   Foot     Create the doc; once made, Open in Google Docs and Next: the
//            next doc the plan wants, or Done.
//
// Every control here reads DOCS_WRITE_ON through its host; the sheet also
// says so itself when the switch is off, so a stray mount writes nothing.

import { useEffect, useId, useMemo, useState } from "react";
import { CheckIcon, ExternalLinkIcon, FilePlus2Icon, FileTextIcon, Link2Icon } from "lucide-react";
import { Link } from "react-router-dom";
import {
    DOCS_WRITE_ON,
    SKILL_NOTE_MAX,
    createDocument,
    getCreateTargets,
    getDocTemplates,
    type CreateDocIn,
    type CreateDocKind,
    type CreateDocResult,
    type CreateTarget,
    type DocTemplate,
} from "@service/aeo/clientDocsWrite";
import type { BusinessDocuments, DocsFailure } from "@service/aeo/clientDocs";
import { getEngagementIfExists, type OutletName } from "@service/aeo/clientReview";
import { OUTLET_INVALID_TEXT, customLabel, customLabelProblem, docDateOf, isShortOutlet, outletOk } from "@service/aeo/clientDocsRules";
import { docFillState } from "../../../docs/fillState";
import { runError, runStatus } from "../../../docs/skillRun";
import { useSkillRun } from "../../../docs/useSkillRun";
import { cn } from "../../../lib/cn";
import {
    DrawerBlock,
    DrawerFoot,
    DrawerHead,
    DrawerNotice,
    DrawerPulse,
    FOCUS_RING,
    FamilyChip,
    IconSquare,
    LATE_TEXT,
    PanelIcon,
    PillButton,
    PillLink,
    SURFACE,
    TYPE,
    Tag,
    type DrawerFootMessage,
    type DrawerStatus,
} from "@oura/ui";
import { codeFamily, codeLabel, codeRowName, docsFailureText, staffDocTitle } from "@oura/docs";
import { BOARD_GROUP, docToSendName, groupBoard, groupOfDoc, myToday } from "../boardModel";
import { RadioList, type RadioOption } from "./RadioList";
import {
    CREATE_KINDS,
    CUSTOM_KINDS,
    GDOC_LINK_RE,
    KIND_LABEL,
    SKILLS_PATH,
    SKILL_PLUGS_IN,
    listWords,
    useSkillInputs,
    contractCodes,
    customLabelsOf,
    knownOutlets,
    outletChoices,
    namePreview,
    planMonthOn,
    outletPlace,
    planMonthText,
    prefillFromToSend,
    sameDocOnBoard,
    wordFiles,
    type CreatePrefill,
} from "./createModel";

const FIELD = cn(
    TYPE.tb,
    "block h-11 w-full rounded-xl border-0 bg-gray-50 px-3 text-gray-900 placeholder:text-gray-400 focus:bg-white focus:outline-none focus:ring-2 focus:ring-blue-600 sm:h-9",
);
// Wide enough for a place name, and NOT upper-cased: "Mont Kiara" must keep
// its case, and the backend refuses "mk" (#3973).
const OUTLET_FIELD = cn(FIELD, "w-full sm:w-64");

/** Doc types not offered in "Doc type" for now (ops, 5 Nov 2026). */
const HIDDEN_DOC_TYPES = new Set(["Monthly Performance Report", "Monthly Work Snapshot"]);

/**
 * The recurring docs, told apart in one line each: the build's new pages
 * once, or this month's results and work. The retired names stay on the list so a board
 * still serving them reads the same.
 */
const COPY_PACK_WHICH: Record<string, string> = {
    "Copy Pack": "The wording for the new pages the plan builds, once.",
    "Monthly Update": "This month's work and its wording, a new one each month.",
    "Monthly Performance Report": "This month's results: AI Check, Site Check, Scoreboard and Competitor Watch.",
    "Monthly Work Snapshot": "This month's finished work, changes, deliverables and their evidence.",
    "GBP Changes": "What we changed on the outlet's Google profile, once it is done.",
    "Technical Crawl": "The technical fixes the crawl found, and when they were fixed.",
    "Copy pack for new pages": "The wording for the new pages the plan builds, once.",
    "Monthly copy pack": "The wording for this month's pages, a new one each month.",
};

/** A new doc's status line: never shared on create. */
const NOT_SHARED: DrawerStatus = { tone: "gray", label: "Not shared", clock: { text: "Read it before you share" } };

type Load<T> = { state: "loading" } | { state: "ready"; data: T } | { state: "failed"; error: DocsFailure };

/** One read on mount, with Try again. */
function useRead<T>(read: () => Promise<{ ok: true; data: T } | DocsFailure>, key: string): Load<T> & { retry: () => void } {
    const [load, setLoad] = useState<Load<T>>({ state: "loading" });
    const [attempt, setAttempt] = useState(0);
    useEffect(() => {
        let live = true;
        setLoad({ state: "loading" });
        void read().then((r) => {
            if (!live) return;
            setLoad(r.ok ? { state: "ready", data: r.data } : { state: "failed", error: r });
        });
        return () => {
            live = false;
        };
        // `read` is a fresh closure each render; `key` names what it reads.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [key, attempt]);
    return { ...load, retry: () => setAttempt((n) => n + 1) };
}

/** What a failed Create says, in plain words. */
function createFailureText(f: DocsFailure): string {
    // No answer at all: nothing to say but try again. An answer (409, 500...) says why, in the server's words.
    if (f.reason === "network") return f.status === null ? "The doc was not created. Try again." : f.message;
    if (f.reason === "notServed") return "Oura can't create docs here yet.";
    return docsFailureText(f, "the folder");
}

export function CreateDocSheet({
    businessId,
    businessName,
    board,
    prefill,
    onOpenDoc,
    onNext,
    onClose,
    onBusy,
}: {
    businessId: string;
    businessName: string;
    /** The board as read: codes in the contract, Word files, docs still to send. */
    board: BusinessDocuments | null;
    prefill: CreatePrefill;
    /** Show a doc in the board's drawer: the one just made, or the one a refusal names. */
    onOpenDoc: (id: string) => void;
    /** Start again on the next doc the plan wants. */
    onNext: (prefill: CreatePrefill) => void;
    onClose: () => void;
    onBusy?: (busy: boolean) => void;
}) {
    const templates = useRead(getDocTemplates, "templates");
    const targets = useRead(() => getCreateTargets(businessId), businessId);
    // The outlets the plan names, for the Outlet dropdown. null is unknown: no
    // engagement, names not confirmed, or a read that failed or is pending. The
    // field then stays a text box as before.
    const [planOutlets, setPlanOutlets] = useState<{ businessId: string; names: OutletName[] | null } | null>(null);
    useEffect(() => {
        let live = true;
        setPlanOutlets(null);
        getEngagementIfExists(businessId)
            .then((e) => {
                if (live) setPlanOutlets({ businessId, names: e && Array.isArray(e.outletNames) ? e.outletNames : null });
            })
            .catch(() => {
                if (live) setPlanOutlets({ businessId, names: null });
            });
        return () => {
            live = false;
        };
    }, [businessId]);
    const today = useMemo(() => myToday(), []);

    // A custom doc type opens on the Custom row, its name in the field (ourteam-backend#6516).
    const [docType, setDocType] = useState<string | null>(prefill.customType ? "Custom" : prefill.docType);
    const [label, setLabel] = useState(prefill.customType ?? "");
    const [kind, setKind] = useState<CreateDocKind>(prefill.kind ?? (prefill.customType ? "blank" : "template"));
    const [codes, setCodes] = useState<string[] | null>(prefill.codes.length ? prefill.codes : null);
    const [outlet, setOutlet] = useState(prefill.outlet ?? "");
    const [folderId, setFolderId] = useState<string | null>(null);
    const [linkUrl, setLinkUrl] = useState("");
    const [fileId, setFileId] = useState<string | null>(prefill.fileId ?? null);
    const [busy, setBusy] = useState(false);
    const [refusal, setRefusal] = useState<DocsFailure | null>(null);
    const [fieldError, setFieldError] = useState<{ field: "link" | "outlet" | "file" | "codes" | "note" | "label" | "nameDate"; text: string } | null>(null);
    /** The date in the file name (ourteam-backend#6556): today, or the day a doc made before Oura was really made. */
    const [nameDate, setNameDate] = useState<string>(() => myToday());
    const [created, setCreated] = useState<CreateDocResult | null>(null);
    /** The outlet's place name, typed; empty takes the one the board already knows. */
    const [placeTyped, setPlaceTyped] = useState("");
    /** kind "skill": a note for the skill, optional (#3983). */
    const [note, setNote] = useState("");
    // A skill's run, kept current after Create: the draft lands in the doc when it ends, so
    // "being written" turns into "done" or "failed" here without opening the doc. Called
    // before the early returns below: a hook cannot sit after one.
    const run = useSkillRun(created?.document.id ?? null, created?.document.docType ?? null, {
        enabled: Boolean(created?.run),
        initial: created?.run ?? null,
        expected: true,
    });

    const typeId = useId();
    const howId = useId();
    const codesId = useId();
    const folderHeadId = useId();
    const linkId = useId();
    const wordId = useId();
    const outletId = useId();
    const noteId = useId();
    const labelId = useId();
    const errorId = useId();

    const list = templates.state === "ready" ? templates.data : [];
    const template: DocTemplate | null = list.find((t) => t.docType === docType) ?? null;
    // A doc type staff name themselves (ourteam-backend#6516): its name is the label typed.
    const isCustom = template?.custom === true;
    const labelText = customLabel(label);
    const labelProblem = isCustom ? customLabelProblem(label) : null;
    const typeName = isCustom ? labelText : template?.docType ?? "";
    const usedLabels = isCustom ? customLabelsOf(board) : [];
    const skillInputs = useSkillInputs(docType);
    // A prefilled type with no template opens on "From a Google Doc we have", as picking it would.
    // Not a custom doc type: it has no template by design, and opens on a blank Google Doc.
    const noTemplate = Boolean(template && !template.templateFileId && !isCustom);
    useEffect(() => {
        if (noTemplate) setKind((k) => (k === "template" ? "link" : k));
    }, [noTemplate]);
    const words = wordFiles(board);
    const targetList: CreateTarget[] = targets.state === "ready" ? targets.data : [];
    const root = targetList.find((t) => t.root) ?? targetList[0] ?? null;
    const folder = targetList.find((t) => t.folderId === folderId) ?? root;

    // The codes on: the prefill's, else the template's that the contract sold. A custom doc
    // type offers every code in the plan (the Execution Timeline once used), none ticked.
    const planCodes = Array.from(new Set((board?.codes ?? []).filter((r) => r.inContract).map((r) => r.code)));
    const offered = template ? (isCustom ? planCodes : template.codes) : [];
    const picked = (codes ?? (template && !isCustom ? contractCodes(template, board) : [])).filter((c) => offered.includes(c));
    const perOutlet = Boolean(template?.perOutlet);
    // Trimmed, never re-cased: the backend refuses "mk" and keeps "Mont Kiara"
    // exactly as typed, so the field must not quietly fix either one.
    const typedOutlet = outlet.trim();
    const outlets = template ? knownOutlets(template.codes, board) : [];
    const outletList = outletChoices(planOutlets?.businessId === businessId ? planOutlets.names : null, outlets, typedOutlet);
    // A custom doc may name an outlet too, one of the plan's, never a must (ourteam-backend#6516).
    const outletOn = (perOutlet || isCustom) && outletOk(typedOutlet) ? typedOutlet : null;
    // An outlet written as its place name IS the place: line 1 and the file
    // name take it, and the separate "Outlet name" field would ask for the
    // same thing twice, so it only shows for a short code.
    const outletIsPlace = Boolean(outletOn) && !isShortOutlet(outletOn);
    const knownPlace = outletPlace(board, outletOn);
    const place = outletIsPlace ? outletOn : outletOn ? placeTyped.trim() || knownPlace : null;
    // A monthly doc is named with the plan month of its naming day (ourteam-backend#6868).
    const nameMonthOn = template?.eachMonth ? planMonthOn(board, nameDate || today) : null;
    const preview = template && typeName && !labelProblem ? namePreview(businessName, typeName, picked, outletOn, nameDate || today, null, place, nameMonthOn) : null;
    // A custom doc's name before its label is typed (staging, 27 Oct): the same name with the type held open.
    const shown = preview ?? (template && isCustom ? namePreview(businessName, "<doc type>", picked, outletOn, nameDate || today, null, place) : null);
    const dup = template && typeName && !labelProblem ? sameDocOnBoard(board, typeName, picked, outletOn, kind === "convert" ? fileId : null) : null;

    const pickType = (t: string) => {
        setDocType(t);
        // Every type change drops the codes picked: a catalogue row then ticks its contract
        // codes, and a custom doc type none (`picked` falls back to [] for it).
        setCodes(null);
        setFieldError(null);
        setRefusal(null);
        const next = list.find((x) => x.docType === t);
        if (next && !next.perOutlet) setOutlet("");
        if (next?.custom) setKind("blank");
        else if (kind === "blank") setKind(next?.templateFileId ? "template" : "link");
        else if (next && !next.templateFileId && kind === "template") setKind("link");
    };
    const toggleCode = (c: string) => {
        setCodes(picked.includes(c) ? picked.filter((x) => x !== c) : offered.filter((x) => x === c || picked.includes(x)));
        setFieldError(null);
        setRefusal(null);
    };

    const create = async () => {
        if (!template || !folder || busy || !DOCS_WRITE_ON) return;
        // Docs carry no version (#4874): the same codes and outlet open the doc already there.
        if (dup) return onOpenDoc(dup.id);
        if (isCustom && labelProblem) return setFieldError({ field: "label", text: labelProblem });
        if (picked.length === 0) return setFieldError({ field: "codes", text: "Pick at least 1 code." });
        if (perOutlet && typedOutlet && !outletOk(typedOutlet)) return setFieldError({ field: "outlet", text: OUTLET_INVALID_TEXT });
        if (kind === "link" && !GDOC_LINK_RE.test(linkUrl.trim())) return setFieldError({ field: "link", text: "Paste the link to a Google Doc. It starts docs.google.com/document." });
        if (kind === "convert" && !fileId) return setFieldError({ field: "file", text: "Pick the Word file." });
        if (kind === "skill" && note.trim().length > SKILL_NOTE_MAX) return setFieldError({ field: "note", text: `A note is ${SKILL_NOTE_MAX} characters or fewer.` });
        if (!/^\d{4}-\d{2}-\d{2}$/.test(nameDate)) return setFieldError({ field: "nameDate", text: "Pick the date for the name." });
        if (nameDate > today) return setFieldError({ field: "nameDate", text: "The date in the name can't be after today." });
        const body: CreateDocIn = {
            kind,
            docType: template.docType,
            // Set, it replaces docType (ourteam-backend#6516).
            ...(isCustom ? { customType: labelText } : {}),
            codes: picked,
            outlet: (perOutlet || isCustom) && typedOutlet ? typedOutlet : null,
            folderId: folder.folderId,
            linkUrl: kind === "link" ? linkUrl.trim() : null,
            fileId: kind === "convert" ? fileId : null,
            note: kind === "skill" && note.trim() ? note.trim() : null,
            // Line 1 names the outlet ("GBP Pack, Mont Kiara (S2-MK)"); without a place, the server's default.
            title: place && preview ? preview.title : null,
            // Sent only when it is not today, so a doc named today posts exactly as before.
            ...(nameDate !== today ? { nameDate } : {}),
        };
        setBusy(true);
        onBusy?.(true);
        setRefusal(null);
        setFieldError(null);
        const r = await createDocument(businessId, body);
        setBusy(false);
        onBusy?.(false);
        if (!r.ok) {
            if (r.code === "link_not_a_doc") setFieldError({ field: "link", text: r.message });
            else if (r.code === "file_not_word") setFieldError({ field: "file", text: r.message });
            else if (r.code === "outlet_invalid" || r.code === "outlet_unknown") setFieldError({ field: "outlet", text: r.message });
            else if (r.code === "custom_type_invalid") setFieldError({ field: "label", text: r.message });
            else if (r.code === "code_not_in_plan") setFieldError({ field: "codes", text: r.message });
            else if (r.code === "name_date_in_future") setFieldError({ field: "nameDate", text: r.message });
            else setRefusal(r);
            return;
        }
        setCreated(r.data);
    };

    if (!DOCS_WRITE_ON) {
        return (
            <>
                <DrawerHead title="Create a doc" />
                <DrawerBlock>
                    <p className="m-0 text-gray-700">Oura can&rsquo;t create docs here yet.</p>
                </DrawerBlock>
            </>
        );
    }

    /* ---- Made: read it before you share, then the next doc. ---- */
    if (created) {
        const d = created.document;
        // A server without ourteam-backend#6556 ignores `nameDate` and names the file today (Jack, #4715):
        // said here, at once, rather than left for staff to find in Drive.
        const wantedDay = nameDate !== today ? docDateOf(nameDate) : null;
        const dateMissed = wantedDay !== null && !d.name.includes(`_${wantedDay}_`);
        const toSend = board ? groupBoard(board).toSend : [];
        // The next doc still owed: one with a row the new doc does not cover. A row is
        // covered by its code AND its outlet, so another outlet's pack of the same type
        // (S2 for BB after the pack for MK) is still owed, and the foot offers it rather
        // than saying Done. A row with no outlet, or a doc made for no outlet, goes by code.
        const covers = (r: { code: string; outlet?: string | null }) => d.codes.includes(r.code) && (!r.outlet || !d.outlet || r.outlet === d.outlet);
        const nextDoc = toSend.find((t) => t.docType && !t.rows.every(covers));
        const nextOutlet = nextDoc?.rows.find((r) => r.outlet)?.outlet ?? null;
        const nextPlace = nextOutlet ? outletPlace(board, nextOutlet) ?? nextOutlet : null;
        // Whether it has its content yet: docFillState in docs/fillState.ts,
        // which also records why no timer is involved (#4051).
        // A skill's run, kept current by `useSkillRun` above (it answers with what Create
        // returned until the library lists it, then follows it).
        const fill = docFillState(kind, run);
        const fillFailed = fill === "failed";
        const filling = fill === "filling";
        const bySkill = Boolean(run);
        const status: DrawerStatus = fillFailed
            ? { tone: "rose", label: "Not shared", clock: { text: bySkill ? "The skill run did not finish" : "The fill did not finish", late: true } }
            : filling
                ? { tone: "gray", label: "Not shared", clock: { text: bySkill ? "Oura is writing the draft…" : "Filling in from this client's data" } }
                : NOT_SHARED;
        const message: DrawerFootMessage = fillFailed
            ? { tone: "error", text: bySkill ? "Created, but the draft was not written. The client can't see it." : "Created, but not filled in. The client can't see it." }
            : filling
                ? { tone: "info", text: bySkill ? "Created, and the draft is being written. The client can't see it." : "Created, and being filled in. The client can't see it." }
                : { tone: "success", text: "Created. The client can't see it." };
        return (
            <>
                <DrawerHead
                    lead={<IconSquare tone="blue" icon={FileTextIcon} />}
                    leadKind="square"
                    title={staffDocTitle(d)}
                    status={status}
                    chips={<CodeChips codes={d.codes} outlet={d.outlet} />}
                    clampTitle
                />
                <DrawerBlock label="File">
                    <p className={cn(TYPE.tc, "m-0 break-words font-mono text-gray-900")}>{d.name}</p>
                    {dateMissed && (
                        <p role="status" className={cn(TYPE.tc, "m-0 mt-1 font-medium text-amber-800")}>
                            Named with today&rsquo;s date, not {wantedDay}: this server does not take the date yet. Rename the file in Drive.
                        </p>
                    )}
                    {folder && <p className={cn(TYPE.tc, "m-0 mt-1 text-gray-500")}>In {folder.path}. Only ourteam can open it.</p>}
                </DrawerBlock>
                {fillFailed ? (
                    <DrawerBlock>
                        <p className="m-0 font-medium text-rose-700">{run ? runStatus(run).label : "Oura could not fill it in"}</p>
                        {/* The server's own sentence, as written. */}
                        {run && runError(run) && <p className={cn(TYPE.tc, "m-0 mt-0.5 text-gray-700")}>{runError(run)}</p>}
                        <p className={cn(TYPE.tc, "m-0 mt-1 text-gray-500")}>
                            {run ? "The doc exists and holds a placeholder." : "The doc exists and still reads as the template."} Nothing has been shared.
                        </p>
                    </DrawerBlock>
                ) : filling ? (
                    <DrawerBlock>
                        <p className="m-0 font-medium text-gray-900">{bySkill ? "Oura is writing the draft…" : "Oura is filling it in"}</p>
                        <p className={cn(TYPE.tc, "m-0 mt-0.5 text-gray-500")}>
                            {bySkill
                                ? "The skill writes it into this doc. Until it finishes the doc holds a placeholder, so there is nothing to read yet."
                                : "From this client’s data, which takes about a minute. Until it finishes the doc still reads as the template, so there is nothing to read yet."}
                        </p>
                    </DrawerBlock>
                ) : (
                    <DrawerBlock>
                        <p className="m-0 font-medium text-gray-900">Read it before you share</p>
                        <p className={cn(TYPE.tc, "m-0 mt-0.5 text-gray-500")}>Sharing is its own step, once someone at ourteam has read it.</p>
                        <PillButton variant="text" className="mt-2" onClick={() => onOpenDoc(d.id)}>
                            Open it in Oura
                        </PillButton>
                    </DrawerBlock>
                )}
                {/* A FAILED FILL AND A RUNNING ONE BOTH LEAD AWAY FROM GOOGLE
                    DOCS, because what is there is the template.
                    - failed: the doc's own row in Oura, where the failure is
                      handled and a re-run will live.
                    - filling: the next doc, with Google Docs kept as the
                      second button so a staffer who wants to look still can.
                    - neither: unchanged, Google Docs first.
                    The second button stays the next doc (or Done) in every
                    case but a running fill, so moving on never costs a click
                    it did not cost before. */}
                <DrawerFoot
                    message={message}
                    first={fillFailed
                        ? (
                            <PillButton variant="primary" onClick={() => onOpenDoc(d.id)}>
                                Open it in Oura
                            </PillButton>
                        )
                        : filling
                        ? (nextDoc ? (
                            <PillButton variant="primary" writes onClick={() => onNext(prefillFromToSend(nextDoc))} aria-label={`Next: create the ${docToSendName(nextDoc)}`}>
                                Next: {nextDoc.docType}
                                {nextPlace ? ` for ${nextPlace}` : ""}
                            </PillButton>
                        ) : (
                            <PillButton variant="primary" onClick={onClose}>Done</PillButton>
                        ))
                        : (
                            <PillLink variant="primary" href={d.url} target="_blank" rel="noopener noreferrer" icon={ExternalLinkIcon}>
                                Open in Google Docs
                                <span className="sr-only">, opens in a new tab</span>
                            </PillLink>
                        )}
                    second={filling
                        ? (
                            <PillLink href={d.url} target="_blank" rel="noopener noreferrer" icon={ExternalLinkIcon}>
                                Open in Google Docs
                                <span className="sr-only">, opens in a new tab. It reads as the template until the fill finishes</span>
                            </PillLink>
                        )
                        : nextDoc ? (
                            <PillButton writes onClick={() => onNext(prefillFromToSend(nextDoc))} aria-label={`Next: create the ${docToSendName(nextDoc)}`}>
                                Next: {nextDoc.docType}
                                {nextPlace ? ` for ${nextPlace}` : ""}
                            </PillButton>
                        ) : (
                            <PillButton onClick={onClose}>Done</PillButton>
                        )
                    }
                />
            </>
        );
    }

    /* ---- The form. ---- */
    // Hidden from the choice for now (ops, 5 Nov 2026); a sheet opened on one of them
    // (a "Next:" from the docs to send) still shows it, so it is never a blank pick.
    const typeOptions: RadioOption[] = list.filter((t) => !HIDDEN_DOC_TYPES.has(t.docType) || t.docType === docType).map((t) => t.custom ? {
        key: t.docType,
        label: "Custom doc type",
        sub: "A doc outside Oura's doc types, named by you. Any of the plan's codes, and an outlet if you want one.",
    } : {
        key: t.docType,
        label: t.docType,
        sub: (
            <span className="flex flex-wrap items-center gap-1">
                {t.codes.map((c) => (
                    <FamilyChip key={c} family={codeFamily(c)}>
                        {c}
                    </FamilyChip>
                ))}
                {t.perOutlet && <span className="ml-1">1 per outlet</span>}
                {t.eachMonth && <span className="ml-1">1 each month</span>}
                {COPY_PACK_WHICH[t.docType] && <span className="ml-1 basis-full">{COPY_PACK_WHICH[t.docType]}</span>}
            </span>
        ),
    });

    // The row's own ways when the backend lists them; a custom doc type has no template or skill.
    const kinds = template?.kinds ?? (isCustom ? CUSTOM_KINDS : CREATE_KINDS);
    const kindOptions: RadioOption[] = kinds.map((k) => {
        if (k === "blank") {
            return {
                key: k,
                label: KIND_LABEL.blank,
                sub: "An empty Google Doc with its name and lines 1 and 2, to write in.",
            };
        }
        if (k === "skill") {
            // Open once a skill is added for the doc type (`template.skill === "added"`);
            // until then shown, shut, and where it plugs in.
            const added = template?.skill === "added";
            return {
                key: k,
                label: (
                    <span className="inline-flex flex-wrap items-center gap-2">
                        {KIND_LABEL.skill}
                        {!added && <Tag tone="gray">No skill added yet</Tag>}
                    </span>
                ),
                sub: (
                    <>
                        {added ? "The skill writes the draft into a new doc. " : `${SKILL_PLUGS_IN} `}
                        {skillInputs ? `Oura sends it ${listWords(skillInputs.map((w) => w.charAt(0).toLowerCase() + w.slice(1)))}.` : "Its slot lists what Oura sends it."}{" "}
                        <Link to={SKILLS_PATH} className={cn("font-medium text-blue-700 hover:underline", FOCUS_RING)}>
                            Open the Skill library
                        </Link>
                    </>
                ),
                disabled: !added,
                extra: added ? (
                    <>
                        <label htmlFor={noteId} className={cn(TYPE.tc, "m-0 block font-medium text-gray-900")}>
                            A note for the skill <span className="font-normal text-gray-400">(optional)</span>
                        </label>
                        <textarea
                            id={noteId}
                            value={note}
                            rows={3}
                            maxLength={SKILL_NOTE_MAX}
                            placeholder="Shorter answers, add the halal line"
                            aria-invalid={fieldError?.field === "note" || undefined}
                            aria-describedby={fieldError?.field === "note" ? errorId : undefined}
                            onChange={(e) => {
                                setNote(e.target.value);
                                if (fieldError?.field === "note") setFieldError(null);
                            }}
                            className={cn(FIELD, "mt-1 h-auto min-h-[5rem] py-2")}
                        />
                        <p className={cn(TYPE.tc, "m-0 mt-1 text-gray-500")}>{note.length} of {SKILL_NOTE_MAX}</p>
                        {fieldError?.field === "note" && <FieldError id={errorId} text={fieldError.text} />}
                    </>
                ) : undefined,
            };
        }
        if (k === "template") {
            const missing = Boolean(template && !template.templateFileId);
            return {
                key: k,
                label: KIND_LABEL.template,
                sub: missing ? "No template for this doc type yet." : template ? `A copy of the ${template.docType} template.` : "A copy of the doc type's template.",
                disabled: missing,
            };
        }
        if (k === "link") {
            return {
                key: k,
                label: KIND_LABEL.link,
                sub: "Oura names it and files it under these codes.",
                extra: (
                    <>
                        <label htmlFor={linkId} className={cn(TYPE.tc, "m-0 block font-medium text-gray-900")}>
                            Its link
                        </label>
                        <input
                            id={linkId}
                            type="url"
                            inputMode="url"
                            value={linkUrl}
                            autoComplete="off"
                            placeholder="https://docs.google.com/document/d/..."
                            aria-invalid={fieldError?.field === "link" || undefined}
                            aria-describedby={fieldError?.field === "link" ? errorId : undefined}
                            onChange={(e) => {
                                setLinkUrl(e.target.value);
                                if (fieldError?.field === "link") setFieldError(null);
                            }}
                            className={cn(FIELD, "mt-1")}
                        />
                        {fieldError?.field === "link" && <FieldError id={errorId} text={fieldError.text} />}
                    </>
                ),
            };
        }
        return {
            key: k,
            label: KIND_LABEL.convert,
            sub: words.length ? "Oura makes a Google Doc from it. The Word file stays." : "No Word file in the folder.",
            disabled: words.length === 0,
            extra: (
                <>
                    <p id={wordId} className={cn(TYPE.tc, "m-0 font-medium text-gray-900")}>
                        Which Word file
                    </p>
                    <RadioList
                        labelId={wordId}
                        value={fileId}
                        onPick={(id) => {
                            setFileId(id);
                            if (fieldError?.field === "file") setFieldError(null);
                        }}
                        options={words.map((w) => ({ key: w.driveFileId, label: w.name }))}
                    />
                    {fieldError?.field === "file" && <FieldError id={errorId} text={fieldError.text} />}
                </>
            ),
        };
    });

    const folderOptions: RadioOption[] = targetList.map((t) => ({
        key: t.folderId,
        label: t.path,
        sub: t.sharedWithClient ? (
            <span className="font-medium text-amber-800">
                The client can open this folder{t.clientEmails.length ? ` (${t.clientEmails.join(", ")})` : ""}.
            </span>
        ) : t.root ? (
            "The client's folder. Only ourteam can open it."
        ) : undefined,
    }));

    const kindOpen = kindOptions.some((o) => o.key === kind && !o.disabled);
    const ready = templates.state === "ready" && Boolean(template) && Boolean(folder) && picked.length > 0 && kindOpen && !labelProblem;
    const title = isCustom ? (labelText && !labelProblem ? `Create the ${labelText}` : "Create a custom doc") : template ? `Create the ${template.docType}` : "Create a doc";
    const existingId = refusal?.code === "doc_exists_unshared" ? refusal.existingId : null;
    /**
     * A refusal belongs at the button that was pressed.
     *
     * It used to show only in a notice at the TOP of the sheet, and this sheet
     * is seven blocks long -- doc type, how, codes, folder, the link or the
     * Word file, the outlet, the name preview. Pressing "Create the doc" at the
     * foot of all that put the server's sentence somewhere off screen, and
     * nothing was said at the button or through the live region, so a 409
     * `doc_exists_unshared` read as the button doing nothing at all.
     *
     * The foot is where every other write view in this module says a refusal
     * (QuestionsView, NameLinesView, ShareView, DocMessageView), and `DrawerFoot`
     * announces its message, so it is read out as well as seen. The doc the
     * refusal names is opened from here too, since there is no longer a notice
     * above to carry it.
     */
    const refusalMessage: DrawerFootMessage | null = refusal
        ? {
            tone: "error",
            text: (
                <>
                    {createFailureText(refusal)}
                    {existingId && (
                        <>
                            {" "}
                            <PillButton variant="text" onClick={() => onOpenDoc(existingId)}>
                                Open that doc
                            </PillButton>
                        </>
                    )}
                </>
            ),
        }
        : null;

    return (
        <>
            <DrawerHead
                lead={<PanelIcon icon={FilePlus2Icon} area="setup" size={16} />}
                title={title}
                status={NOT_SHARED}
                chips={picked.length > 0 ? <CodeChips codes={picked} outlet={outletOn} /> : undefined}
            />

            <DrawerBlock>
                <p id={typeId} className="m-0 font-medium text-gray-900">
                    Doc type
                </p>
                {templates.state === "loading" && <DrawerPulse lines={3} label="Loading the doc types" />}
                {templates.state === "failed" && (
                    <p className={cn(TYPE.tc, "m-0 mt-1 text-gray-600")}>
                        {templates.error.reason === "notServed" ? "Oura can't list the doc types here yet." : "Could not load the doc types."}{" "}
                        {templates.error.reason !== "notServed" && (
                            <PillButton variant="text" onClick={templates.retry}>
                                Try again
                            </PillButton>
                        )}
                    </p>
                )}
                {templates.state === "ready" && <RadioList labelId={typeId} options={typeOptions} value={docType} onPick={pickType} />}
            </DrawerBlock>

            {template && (
                <>
                    {isCustom && (
                        <DrawerBlock>
                            <label htmlFor={labelId} className="m-0 block font-medium text-gray-900">
                                Doc type name
                            </label>
                            <p className={cn(TYPE.tc, "m-0 mt-0.5 text-gray-500")}>
                                What the client calls it, such as Treatment Menu. It goes in the file name where the doc type sits.
                            </p>
                            <input
                                id={labelId}
                                value={label}
                                maxLength={60}
                                autoComplete="off"
                                placeholder="Treatment Menu"
                                aria-invalid={Boolean(label && labelProblem) || fieldError?.field === "label" || undefined}
                                aria-describedby={fieldError?.field === "label" || (label && labelProblem) ? errorId : undefined}
                                onChange={(e) => {
                                    setLabel(e.target.value);
                                    if (fieldError?.field === "label") setFieldError(null);
                                    setRefusal(null);
                                }}
                                className={cn(FIELD, "mt-2")}
                            />
                            {usedLabels.length > 0 && (
                                <div className="mt-2 flex flex-wrap items-center gap-2">
                                    <span className={cn(TYPE.tc, "text-gray-500")}>Used for this client:</span>
                                    {usedLabels.map((l) => (
                                        <PillButton key={l} size="sm" onClick={() => setLabel(l)} aria-pressed={labelText.toLowerCase() === l.toLowerCase()}>
                                            {l}
                                        </PillButton>
                                    ))}
                                </div>
                            )}
                            {fieldError?.field === "label" ? (
                                <FieldError id={errorId} text={fieldError.text} />
                            ) : (
                                label.trim() && labelProblem && <FieldError id={errorId} text={labelProblem} />
                            )}
                        </DrawerBlock>
                    )}

                    <DrawerBlock>
                        <p id={howId} className="m-0 font-medium text-gray-900">
                            How
                        </p>
                        <RadioList
                            labelId={howId}
                            options={kindOptions}
                            value={kind}
                            onPick={(k) => {
                                setKind(k as CreateDocKind);
                                setFieldError(null);
                                setRefusal(null);
                            }}
                        />
                    </DrawerBlock>

                    <DrawerBlock>
                        <p id={codesId} className="m-0 font-medium text-gray-900">
                            Codes
                        </p>
                        <p className={cn(TYPE.tc, "m-0 mt-0.5 text-gray-500")}>
                            {isCustom
                                ? offered.length
                                    ? "The plan's codes this doc clears, with the month the plan puts each in."
                                    : "This client has no plan yet, so a custom doc has no code to take."
                                : "The codes this doc clears. Leave out any that go in another doc."}
                        </p>
                        <div role="group" aria-labelledby={codesId} className="mt-2">
                            {offered.map((c) => {
                                const on = picked.includes(c);
                                const row = board?.codes.find((r) => r.code === c && !r.outlet) ?? board?.codes.find((r) => r.code === c) ?? null;
                                return (
                                    <button
                                        key={c}
                                        type="button"
                                        role="checkbox"
                                        aria-checked={on}
                                        onClick={() => toggleCode(c)}
                                        className={cn("flex min-h-11 w-full items-center gap-3 rounded-xl px-3 py-2 text-left hover:bg-gray-50", FOCUS_RING)}
                                    >
                                        <span
                                            aria-hidden
                                            className={cn(
                                                "grid h-4 w-4 shrink-0 place-items-center rounded ring-1 ring-inset",
                                                on ? "bg-blue-600 text-white ring-blue-600" : "bg-white ring-gray-300",
                                            )}
                                        >
                                            {on && <CheckIcon size={12} strokeWidth={3} />}
                                        </span>
                                        <FamilyChip family={codeFamily(c)}>{c}</FamilyChip>
                                        <span className={cn(TYPE.tb, "min-w-0 flex-1 break-words", on ? "text-gray-900" : "text-gray-500")}>
                                            {row?.inContract === false ? "Not in the contract" : row?.label ? codeRowName(row.label, null) : c}
                                        </span>
                                        {isCustom && planMonthText(row?.expectedMonth) && (
                                            <span className={cn(TYPE.tc, "shrink-0 text-gray-500")}>{planMonthText(row?.expectedMonth)}</span>
                                        )}
                                    </button>
                                );
                            })}
                        </div>
                        {fieldError?.field === "codes" && <FieldError id={errorId} text={fieldError.text} />}
                        {isCustom && outletList.length > 0 && (
                            <div className="mt-3 px-3">
                                <label htmlFor={outletId} className={cn(TYPE.tc, "m-0 block font-medium text-gray-900")}>
                                    Outlet <span className="font-normal text-gray-400">(optional)</span>
                                </label>
                                <p className={cn(TYPE.tc, "m-0 mt-0.5 text-gray-500")}>Only if this doc is for one outlet. It goes in the file name and line 1.</p>
                                <select
                                    id={outletId}
                                    value={typedOutlet}
                                    aria-invalid={fieldError?.field === "outlet" || undefined}
                                    aria-describedby={fieldError?.field === "outlet" ? errorId : undefined}
                                    onChange={(e) => {
                                        setOutlet(e.target.value);
                                        if (fieldError?.field === "outlet") setFieldError(null);
                                    }}
                                    className={cn(OUTLET_FIELD, "mt-2")}
                                >
                                    <option value="">No outlet</option>
                                    {outletList.map((o) => (
                                        <option key={o} value={o}>
                                            {o}
                                        </option>
                                    ))}
                                </select>
                                {fieldError?.field === "outlet" && <FieldError id={errorId} text={fieldError.text} />}
                            </div>
                        )}
                        {perOutlet && (
                            <div className="mt-3 px-3">
                                <label htmlFor={outletId} className={cn(TYPE.tc, "m-0 block font-medium text-gray-900")}>
                                    Outlet
                                </label>
                                <p className={cn(TYPE.tc, "m-0 mt-0.5 text-gray-500")}>
                                    {outletList.length > 0
                                        ? "1 doc per outlet. Pick the one this doc is for, or none for a single outlet."
                                        : "1 doc per outlet. Its place name, such as Mont Kiara, or 2 to 4 capital letters, such as MK. Leave it empty for a single outlet."}
                                </p>
                                <div className="mt-2 flex flex-wrap items-center gap-2">
                                    {outletList.length > 0 ? (
                                        <select
                                            id={outletId}
                                            value={typedOutlet}
                                            aria-invalid={fieldError?.field === "outlet" || undefined}
                                            aria-describedby={fieldError?.field === "outlet" ? errorId : undefined}
                                            onChange={(e) => {
                                                setOutlet(e.target.value);
                                                if (fieldError?.field === "outlet") setFieldError(null);
                                            }}
                                            className={OUTLET_FIELD}
                                        >
                                            <option value="">None, a single outlet</option>
                                            {outletList.map((o) => (
                                                <option key={o} value={o}>
                                                    {o}
                                                </option>
                                            ))}
                                        </select>
                                    ) : (
                                        <input
                                            id={outletId}
                                            value={outlet}
                                            maxLength={40}
                                            placeholder="Mont Kiara or MK"
                                            autoComplete="off"
                                            aria-invalid={fieldError?.field === "outlet" || undefined}
                                            aria-describedby={fieldError?.field === "outlet" ? errorId : undefined}
                                            onChange={(e) => {
                                                setOutlet(e.target.value);
                                                if (fieldError?.field === "outlet") setFieldError(null);
                                            }}
                                            className={OUTLET_FIELD}
                                        />
                                    )}
                                    {outletList.length === 0 &&
                                        outlets.map((o) => (
                                            <PillButton key={o} size="sm" onClick={() => setOutlet(o)} aria-pressed={typedOutlet === o}>
                                                {codeLabel(offered[0] ?? "", o)}
                                            </PillButton>
                                        ))}
                                </div>
                                {fieldError?.field === "outlet" && <FieldError id={errorId} text={fieldError.text} />}
                                {outletOn && !outletIsPlace && (
                                    <div className="mt-3">
                                        <label htmlFor={`${outletId}-place`} className={cn(TYPE.tc, "m-0 block font-medium text-gray-900")}>
                                            Outlet name
                                        </label>
                                        <p className={cn(TYPE.tc, "m-0 mt-0.5 text-gray-500")}>In the title, the email and the client&rsquo;s list, so they can tell their outlets apart.</p>
                                        <input
                                            id={`${outletId}-place`}
                                            value={placeTyped}
                                            placeholder={knownPlace ?? "Such as Mont Kiara"}
                                            autoComplete="off"
                                            onChange={(e) => setPlaceTyped(e.target.value)}
                                            className={cn(FIELD, "mt-2")}
                                        />
                                    </div>
                                )}
                            </div>
                        )}
                    </DrawerBlock>

                    <DrawerBlock>
                        <p id={folderHeadId} className="m-0 font-medium text-gray-900">
                            Folder
                        </p>
                        {targets.state === "loading" && <DrawerPulse lines={2} label="Loading the folders" />}
                        {targets.state === "failed" && (
                            <p className={cn(TYPE.tc, "m-0 mt-1 text-gray-600")}>
                                {docsFailureText(targets.error, "the folders")}{" "}
                                <PillButton variant="text" onClick={targets.retry}>
                                    Try again
                                </PillButton>
                            </p>
                        )}
                        {targets.state === "ready" &&
                            (targetList.length === 0 ? (
                                <p className={cn(TYPE.tc, "m-0 mt-1 text-gray-600")}>Link the client&rsquo;s folder first.</p>
                            ) : (
                                <RadioList
                                    labelId={folderHeadId}
                                    options={folderOptions}
                                    value={folder?.folderId ?? null}
                                    onPick={(id) => {
                                        setFolderId(id);
                                        setRefusal(null);
                                    }}
                                />
                            ))}
                    </DrawerBlock>

                    {/* Shown as soon as a type is picked, a custom one included before its label is
                        typed (staging, 27 Oct): the date is set first, the name follows it. */}
                    {template && (
                        <DrawerBlock label="Name">
                            <div className="mb-2 flex flex-wrap items-center gap-2">
                                <label htmlFor={`${errorId}-name-date`} className={cn(TYPE.tb, "text-gray-900")}>
                                    Date in the name
                                </label>
                                <input
                                    id={`${errorId}-name-date`}
                                    type="date"
                                    value={nameDate}
                                    max={today}
                                    disabled={busy}
                                    aria-invalid={fieldError?.field === "nameDate" || undefined}
                                    aria-describedby={fieldError?.field === "nameDate" ? errorId : undefined}
                                    onChange={(e) => {
                                        setNameDate(e.target.value);
                                        if (fieldError?.field === "nameDate") setFieldError(null);
                                    }}
                                    className={cn(FIELD, "w-auto")}
                                />
                                {nameDate !== today && (
                                    <PillButton variant="text" disabled={busy} onClick={() => setNameDate(today)}>
                                        Today
                                    </PillButton>
                                )}
                                {fieldError?.field === "nameDate" && <FieldError id={errorId} text={fieldError.text} />}
                            </div>
                            {shown && (
                                <>
                                    <p className={cn(TYPE.tc, "m-0 break-words font-mono text-gray-900")}>{shown.name}</p>
                                    <div className={cn(SURFACE.inner, "mt-2")}>
                                        <p className={cn(TYPE.tc, "m-0 text-gray-500")}>{shown.versionLine ? "Lines 1 and 2 of the doc" : "Line 1 of the doc"}</p>
                                        <p className={cn(TYPE.tc, "m-0 mt-0.5 break-words text-gray-900")}>{shown.title}</p>
                                        {shown.versionLine && <p className={cn(TYPE.tc, "m-0 break-words text-gray-600")}>{shown.versionLine}</p>}
                                    </div>
                                    {!preview ? (
                                        <p className={cn(TYPE.tc, "m-0 mt-2 text-gray-500")}>The doc type you name above goes where it says &lt;doc type&gt;.</p>
                                    ) : (
                                        kind !== "template" && (
                                            <p className={cn(TYPE.tc, "m-0 mt-2 flex items-start gap-1.5 text-gray-500")}>
                                                <Link2Icon aria-hidden size={14} className="mt-0.5 shrink-0" />
                                                Oura renames the file to this and writes this title.
                                            </p>
                                        )
                                    )}
                                </>
                            )}
                        </DrawerBlock>
                    )}
                </>
            )}

            {dup && (
                <DrawerNotice>
                    {codeLabel(picked[0] ?? dup.codes[0] ?? "", outletOn)} already has a doc ({BOARD_GROUP[groupOfDoc(dup)].style.label}). Open it instead of making another.
                </DrawerNotice>
            )}

            <DrawerFoot
                message={refusalMessage}
                first={dup ? (
                    <PillButton variant="primary" icon={FileTextIcon} onClick={() => onOpenDoc(dup.id)}>
                        Open it
                    </PillButton>
                ) : (
                    <PillButton variant="primary" icon={FilePlus2Icon} busy={busy} writes disabled={!ready} onClick={() => void create()}>
                        Create the doc
                    </PillButton>
                )}
                second={(
                    <PillButton disabled={busy} onClick={onClose}>
                        Cancel
                    </PillButton>
                )}
            />
        </>
    );
}

function CodeChips({ codes, outlet }: { codes: readonly string[]; outlet: string | null }) {
    const host = outlet ? (codes.includes("S2") ? "S2" : codes[0]) : null;
    return (
        <>
            {codes.map((c) => (
                <FamilyChip key={c} family={codeFamily(c)}>
                    {codeLabel(c, c === host ? outlet : null)}
                </FamilyChip>
            ))}
        </>
    );
}

function FieldError({ id, text }: { id: string; text: string }) {
    return (
        <p id={id} role="alert" className={cn(TYPE.tc, LATE_TEXT, "m-0 mt-2")}>
            {text}
        </p>
    );
}
